// Normalize the message first to catch obfuscation
const normalizeMessage = (message: string): string => {
  return message
    // Normalize unicode digits to ASCII (４０３ -> 403)
    .replace(/[\uFF10-\uFF19]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFF10 + 0x30))
    // Normalize word numbers (basic)
    .replace(/\bzero\b/gi, '0')
    .replace(/\bone\b/gi, '1')
    .replace(/\btwo\b/gi, '2')
    .replace(/\bthree\b/gi, '3')
    .replace(/\bfour\b/gi, '4')
    .replace(/\bfive\b/gi, '5')
    .replace(/\bsix\b/gi, '6')
    .replace(/\bseven\b/gi, '7')
    .replace(/\beight\b/gi, '8')
    .replace(/\bnine\b/gi, '9');
};

const stripUrls = (text: string): string =>
  text.replace(/https?:\/\/[^\s]+/g, '');

const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
// Split-email bypass: message containing only @domain.tld (e.g. "@hotmail.com" sent after "user")
const EMAIL_DOMAIN_ONLY_REGEX = /@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

const PHONE_REGEX =
  /(?<![/\w.])(\+?1[-.\s]?)?(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4,6}|\+?[1-9]\d{0,3}[-.\s]\d{2,5}[-.\s]\d{4,10})(?![\w/])/g;

// Spaced out digits: "4 0 3 4 3 4 4 3 4 3" - 10 digits with spaces
const SPACED_DIGITS_REGEX = /\b(\d[\s.]){6,}\d\b/g;

const WHATSAPP_REGEX =
  /(https?:\/\/)?(wa\.me|whatsapp\.com)\/([0-9]{1,4}[0-9]{6,}|qr\/[A-Z0-9]+)/gi;

const WECHAT_REGEX =
  /(https?:\/\/)?(weixin\.qq\.com|wechat\.com)\/[a-zA-Z0-9_-]+/gi;

const LINKEDIN_REGEX =
  /(https?:\/\/)?(www\.)?linkedin\.com\/(in|pub|profile|company)\/[a-zA-Z0-9_-]+/gi;

const TELEGRAM_REGEX =
  /(https?:\/\/)?t\.me\/[a-zA-Z0-9_]+|@[a-zA-Z0-9_]{4,}(\s+on\s+telegram)?/gi;

const DISCORD_REGEX =
  /(https?:\/\/)?discord\.(gg|com\/invite)\/[a-zA-Z0-9]+|discord(app)?\.com\/users\/[0-9]+/gi;

const SNAPCHAT_REGEX =
  /(https?:\/\/)?(www\.)?snapchat\.com\/add\/[a-zA-Z0-9._-]+/gi;

const SOCIAL_HANDLE_REGEX =
  /\b(find|add|follow|dm|message|contact|reach|text|call|snap)\s+(me\s+)?(at|on|@|via)\s+[@]?[a-zA-Z0-9._-]{3,}/gi;

const CALENDLY_REGEX =
  /(https?:\/\/)?calendly\.com\/[a-zA-Z0-9_-]+/gi;

const VENMO_PAYPAL_REGEX =
  /(https?:\/\/)?(venmo\.com|paypal\.me)\/[a-zA-Z0-9_-]+/gi;

const DIGIT_DENSITY_THRESHOLD = 0.25;
const MIN_DIGITS_FOR_DENSITY_CHECK = 7;

const hasHighDigitDensity = (message: string): boolean => {
  const digits = message.replace(/\D/g, '').length;
  const ratio = digits / message.length;
  return ratio > DIGIT_DENSITY_THRESHOLD && digits >= MIN_DIGITS_FOR_DENSITY_CHECK;
};

export interface ValidationResult {
  isValid: boolean;
  violations: {
    emails: string[];
    phoneNumbers: string[];
    spacedDigits: string[];
    whatsappLinks: string[];
    wechatLinks: string[];
    linkedinLinks: string[];
    telegramLinks: string[];
    discordLinks: string[];
    snapchatLinks: string[];
    socialHandles: string[];
    calendlyLinks: string[];
    paymentLinks: string[];
    highDigitDensity: boolean;
  };
}

const mergeEmailViolations = (fullMatches: string[], domainOnlyMatches: string[]): string[] => {
  const seen = new Set<string>(fullMatches);
  const out = [...fullMatches];
  for (const d of domainOnlyMatches) {
    const alreadyCovered = fullMatches.some((f) => f.endsWith(d) && f.length > d.length);
    if (!alreadyCovered && !seen.has(d)) {
      seen.add(d);
      out.push(d);
    }
  }
  return out;
};

export const validateMessage = (message: string): ValidationResult => {
  const normalized = normalizeMessage(message);
  const textOnly = stripUrls(normalized);

  const fullEmails = normalized.match(EMAIL_REGEX) || [];
  const domainOnlyEmails = normalized.match(EMAIL_DOMAIN_ONLY_REGEX) || [];

  const violations = {
    emails: mergeEmailViolations(fullEmails, domainOnlyEmails),
    phoneNumbers: textOnly.match(PHONE_REGEX) || [],
    spacedDigits: textOnly.match(SPACED_DIGITS_REGEX) || [],
    whatsappLinks: normalized.match(WHATSAPP_REGEX) || [],
    wechatLinks: normalized.match(WECHAT_REGEX) || [],
    linkedinLinks: normalized.match(LINKEDIN_REGEX) || [],
    telegramLinks: normalized.match(TELEGRAM_REGEX) || [],
    discordLinks: normalized.match(DISCORD_REGEX) || [],
    snapchatLinks: normalized.match(SNAPCHAT_REGEX) || [],
    socialHandles: normalized.match(SOCIAL_HANDLE_REGEX) || [],
    calendlyLinks: normalized.match(CALENDLY_REGEX) || [],
    paymentLinks: normalized.match(VENMO_PAYPAL_REGEX) || [],
    highDigitDensity: hasHighDigitDensity(textOnly),
  };

  const isValid =
    Object.entries(violations)
      .every(([key, val]) => key === 'highDigitDensity' ? !val : (val as string[]).length === 0);

  return {
    isValid,
    violations
  };
};